import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Badge, Button, Card, Input } from '@ftedn/ui';
import { useAuth } from '../../hooks/useAuth';
import { auth, isUsingFirebaseEmulators, RecaptchaVerifier } from '../../services/firebase';
import './LoginPage.css';

type AuthMode = 'signin' | 'signup';
type SignupMethod = 'phone' | 'email';
type PhoneStep = 'request' | 'verify';

const TEST_PHONE = '+16505553434';
const TEST_CODE = '123456';

function normalizeReferralCode(value: string | null): string | undefined {
  const normalized = value?.trim().toUpperCase();
  return normalized && /^[A-Z0-9]{4,12}$/.test(normalized) ? normalized : undefined;
}

export function LoginPage() {
  const [authMode, setAuthMode] = useState<AuthMode>('signin');
  const [signupMethod, setSignupMethod] = useState<SignupMethod>('phone');
  const [phoneStep, setPhoneStep] = useState<PhoneStep>('request');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const verifierRef = useRef<RecaptchaVerifier | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, loginWithGoogle, signup, startPhoneSignup, confirmPhoneSignup } = useAuth();
  const referralCode = normalizeReferralCode(searchParams.get('ref'));

  const phoneSignupActive = authMode === 'signup' && signupMethod === 'phone';

  useEffect(() => {
    if (location.pathname === '/join') {
      setAuthMode('signup');
      setSignupMethod('email');
    }
  }, [location.pathname]);

  useEffect(() => {
    setError('');
    setPhoneStep('request');
    setVerificationCode('');
  }, [authMode, signupMethod]);

  useEffect(() => {
    if (!phoneSignupActive || verifierRef.current) return undefined;

    const verifier = new RecaptchaVerifier(auth, 'phone-recaptcha', {
      size: 'normal',
    });

    verifierRef.current = verifier;
    verifier.render().catch(() => undefined);

    return () => {
      verifier.clear();
      if (verifierRef.current === verifier) {
        verifierRef.current = null;
      }
    };
  }, [phoneSignupActive]);

  async function handleEmailSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError('');
    setLoading(true);

    const success = authMode === 'signup'
      ? await signup(email, password, displayName, referralCode)
      : await login(email, password);

    setLoading(false);

    if (success) {
      navigate('/dashboard');
      return;
    }

    setError(authMode === 'signup' ? 'We couldn’t create that account yet.' : 'We couldn’t sign you in with those details.');
  }

  async function handlePhoneRequest(event: React.FormEvent) {
    event.preventDefault();

    if (!verifierRef.current) {
      setError('Phone verification is still warming up. Try again.');
      return;
    }

    setError('');
    setLoading(true);
    const success = await startPhoneSignup(phoneNumber, verifierRef.current);
    setLoading(false);

    if (success) {
      setPhoneStep('verify');
      return;
    }

    setError('We couldn’t send that code yet. Double-check the number and try again.');
  }

  async function handlePhoneConfirm(event: React.FormEvent) {
    event.preventDefault();
    setError('');
    setLoading(true);

    const success = await confirmPhoneSignup(verificationCode, displayName, referralCode);

    setLoading(false);

    if (success) {
      navigate('/dashboard');
      return;
    }

    setError('That code didn’t verify. Try the code again.');
  }

  return (
    <div className="login-container">
      <Card className="login-card" padding="lg" tone="elevated">
        <div className="login-header">
          <h1 className="login-logo"><span className="logo-f">f</span><span className="logo-dot">.</span></h1>
          <p className="login-brand-name">first to eedun</p>
          <p className="login-subtitle">{authMode === 'signup' ? 'Create your account' : 'Welcome back'}</p>
        </div>

        <div className="auth-switcher" role="tablist" aria-label="Authentication mode">
          <button className={`auth-switch ${authMode === 'signin' ? 'is-active' : ''}`} onClick={() => setAuthMode('signin')} type="button">
            Sign In
          </button>
          <button className={`auth-switch ${authMode === 'signup' ? 'is-active' : ''}`} onClick={() => setAuthMode('signup')} type="button">
            Create Account
          </button>
        </div>

        {authMode === 'signup' && (
          <div className="method-switcher" role="tablist" aria-label="Signup method">
            <button className={`method-switch ${signupMethod === 'phone' ? 'is-active' : ''}`} onClick={() => setSignupMethod('phone')} type="button">
              Phone OTP
            </button>
            <button className={`method-switch ${signupMethod === 'email' ? 'is-active' : ''}`} onClick={() => setSignupMethod('email')} type="button">
              Email Instead
            </button>
          </div>
        )}

        {authMode === 'signup' && referralCode && (
          <p className="referral-invite-note">Your referral invite is ready.</p>
        )}

        {authMode === 'signup' && signupMethod === 'phone' ? (
          <form className="login-form" id="phone-signup-form" onSubmit={phoneStep === 'request' ? handlePhoneRequest : handlePhoneConfirm}>
            <div className="login-meta">
              <Badge tone="sage">Phone-first</Badge>
              <p>Start with a verified phone and land in FTEDN with alerts ready to go.</p>
            </div>

            <Input
              hint="How we’ll greet you in the app"
              id="displayName"
              label="Your name"
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="Sage Tester"
              required
              value={displayName}
            />

            {phoneStep === 'request' ? (
              <>
                <Input
                  hint="Use E.164 format for best results"
                  id="phoneNumber"
                  label="Mobile number"
                  onChange={(event) => setPhoneNumber(event.target.value)}
                  placeholder="+16505553434"
                  required
                  value={phoneNumber}
                />
                {isUsingFirebaseEmulators() && (
                  <p className="phone-test-hint">
                    Local test mode: use <strong>{TEST_PHONE}</strong> and code <strong>{TEST_CODE}</strong>.
                  </p>
                )}
                <div className="recaptcha-shell">
                  <div id="phone-recaptcha" />
                </div>
                <Button fullWidth id="phone-send-code" loading={loading} type="submit" variant="primary">
                  Send Code
                </Button>
              </>
            ) : (
              <>
                <Input
                  hint="The 6-digit code we just sent"
                  id="verificationCode"
                  label="Verification code"
                  onChange={(event) => setVerificationCode(event.target.value)}
                  placeholder="123456"
                  required
                  value={verificationCode}
                />
                <Button fullWidth id="phone-verify-code" loading={loading} type="submit" variant="navy">
                  Verify & Continue
                </Button>
              </>
            )}

            {error && <p className="login-error">{error}</p>}
          </form>
        ) : (
          <form className="login-form" id="login-form" onSubmit={handleEmailSubmit}>
            {authMode === 'signup' && (
              <Input
                id="displayName"
                label="Your name"
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder="Your name"
                required
                value={displayName}
              />
            )}
            <Input
              id="email"
              label="Email"
              onChange={(event) => setEmail(event.target.value)}
              placeholder="Email"
              required
              type="email"
              value={email}
            />
            <Input
              hint={authMode === 'signup' ? 'Use at least 8 characters' : undefined}
              id="password"
              label="Password"
              minLength={8}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Password"
              required
              type="password"
              value={password}
            />

            {authMode === 'signup' && (
              <p className="email-signup-note">
                Email signup works too. We’ll invite you to add a phone afterward for alerts and streak nudges.
              </p>
            )}

            {error && <p className="login-error">{error}</p>}

            <Button fullWidth id="submit-btn" loading={loading} type="submit" variant="navy">
              {authMode === 'signup' ? 'Create Account' : 'Sign In'}
            </Button>
          </form>
        )}

        <div className="social-divider">
          <span>or continue with</span>
        </div>

        <button
          className="google-signin-btn"
          id="google-signin-btn"
          onClick={async () => {
            setError('');
            setLoading(true);
            const success = await loginWithGoogle(referralCode);
            setLoading(false);
            if (success) {
              navigate('/dashboard');
            } else {
              setError('Google sign-in failed. Try again.');
            }
          }}
          type="button"
          disabled={loading}
        >
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          <span>Sign in with Google</span>
        </button>
      </Card>
    </div>
  );
}
